import { Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { isReportTargetVisible } from "@utils/reportVisibility";
import { getHierarchicalIdentifier, unwrapConnection } from "@utils/utils";
import React from "react";

import { Paragraph } from "@fronterahealth/frontera-ui-components";
import { Field, FieldLabel } from "@fronterahealth/frontera-ui-components/ui/field";

import {
  ApiReportGoalChartTypeChoices,
  ApiReportReportStatusChoices,
  ReportDomainType,
  ReportGoalType,
  ReportTargetType,
} from "@api/graphql/types-and-hooks";
import { GasPanel } from "@components/GoalsSections/GasPanel";
import { useSuggestionCount } from "@components/GoalsSections/ReviewGoalHelper";
import {
  ShortTermGoalProgressGraph,
  selectItemForGoalChartType,
} from "@components/ShortTermGoalPanel/ShortTermGoalProgressGraph";
import { TemplateTargetReviewCard } from "@components/TreatmentPlanFieldForm/TemplateTargetReviewCard";
import { TreatmentPlanForm } from "@components/TreatmentPlanFieldForm/TreatmentPlanForm";
import { useInlineTreatmentPlanEdit } from "@components/TreatmentPlanFieldForm/useInlineTreatmentPlanEdit";
import { useTreatmentPlanTemplate } from "@components/TreatmentPlanFieldForm/useTreatmentPlanTemplate";
import { goalToFieldValues } from "@components/TreatmentPlanFieldForm/utils";
import { ReportNode } from "@providers/ReportProvider";

type TemplateFields = ReturnType<typeof useTreatmentPlanTemplate>;

interface TemplateGoalReviewCardProps {
  goal: ReportGoalType;
  domain: ReportDomainType | null;
  report: ReportNode;
  goalFields: TemplateFields["goalFields"];
  targetFields: TemplateFields["targetFields"];
  isGasEnabled: boolean;
  onGoalMutationSuccess: () => void;
  /** Index used for goal numbering prefix (e.g. "1.", "2.") */
  goalIndex: number;
  showChart?: boolean;
  /** Show the goal's medical necessity. Review and completed views only (PROA-3018). */
  showMedicalNecessity?: boolean;
}

// Medical necessity is a goal column, not a template field, so TreatmentPlanForm never
// renders it. Shown on the export's rule: the goal's own checkbox, not the template setting.
const GoalMedicalNecessityRow: React.FC<{ criteria: string | null | undefined; isReportCompleted: boolean }> = ({
  criteria,
  isReportCompleted,
}) => {
  const labelId = React.useId();
  const text = criteria?.trim();

  let value: React.ReactNode;
  if (text) {
    // Plain text, unlike the masked inputs around it, so mask it for both session recorders.
    value = (
      <span className="ph-no-capture" data-dd-privacy="mask">
        {text}
      </span>
    );
  } else if (isReportCompleted) {
    value = <span className="text-text-secondary">No criteria entered</span>;
  } else {
    // Still fixable in the goal editor, so flag it before signing.
    value = (
      <span className="flex items-center gap-x-1 text-text-warning-primary">
        <ExclamationTriangleIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
        No criteria entered
      </span>
    );
  }

  return (
    <Field data-testid="goal-medical-necessity" aria-labelledby={labelId}>
      <FieldLabel id={labelId}>Medical Necessity</FieldLabel>
      {value}
    </Field>
  );
};

export const TemplateGoalReviewCard: React.FC<TemplateGoalReviewCardProps> = ({
  goal,
  domain,
  report,
  goalFields,
  targetFields,
  isGasEnabled,
  onGoalMutationSuccess,
  goalIndex,
  showChart = false,
  showMedicalNecessity = false,
}) => {
  const learnerId = report.learner?.id || "";
  const reportIsCompleted = report.reportStatus === ApiReportReportStatusChoices.Completed;
  // The export prints it after the goal fields, directly above the graph. This card draws
  // the graph above the fields, so with a graph the row goes directly above it; without
  // one it goes after the fields, before the targets.
  const medicalNecessityRow =
    showMedicalNecessity && goal.hasMedicalNecessity ? (
      <GoalMedicalNecessityRow criteria={goal.medicalNecessityCriteria} isReportCompleted={reportIsCompleted} />
    ) : null;

  const getSuggestionCount = useSuggestionCount(goal.id, "stg");

  const { saveGoalFields, saveGoalCustomField } = useInlineTreatmentPlanEdit({
    goalId: goal.id,
    reportId: report.id,
    domainId: domain?.id ?? null,
    learnerId,
    onSuccess: onGoalMutationSuccess,
  });

  const targets = (unwrapConnection(goal.reporttargetSet) as ReportTargetType[]) || [];
  // Show only in-use, non-excluded targets — the same rule the export applies
  // (get_frontera_report_goals filters in_use=True, exclude_from_report=False),
  // kept in one place via isReportTargetVisible. The IA query (reportGoals)
  // returns unselected targets, so the inUse check is what hides them (PROA-2372).
  // Both feeding queries must fetch `inUse`; if either stops, this hides every
  // target — that was the PROA-2122 regression, not a reason to drop the check.
  const visibleTargets = targets.filter(isReportTargetVisible);

  const [open, setOpen] = React.useState(true);
  const prefix = getHierarchicalIdentifier(goalIndex);

  return (
    <div className="relative flex w-full items-center rounded-md">
      <div className="flex w-full flex-col">
        {/* Header */}
        <div
          className={`relative flex w-full flex-col items-start justify-between bg-white p-8 rounded-2xl ${open ? "border-b-0 rounded-b-none" : ""}`}
          data-stg
        >
          <div className="flex w-full flex-row items-center justify-between gap-x-2">
            <Paragraph colorType="primary" displayType="loud" data-goal-title className="ph-no-capture">
              {prefix && <span className="text-text-secondary">{prefix}</span>}
              {goal.goalName}
            </Paragraph>
            <div className="h-6 w-6 shrink-0">
              {open ? (
                <ChevronUpIcon
                  onClick={() => setOpen(false)}
                  className="absolute h-6 w-6 p-1 text-black hover:cursor-pointer"
                />
              ) : (
                <ChevronDownIcon
                  onClick={() => setOpen(true)}
                  className="absolute h-6 w-6 p-1 text-black hover:cursor-pointer"
                />
              )}
            </div>
          </div>
        </div>

        {/* Expandable Content */}
        <Transition
          as="div"
          show={open}
          className="overflow-hidden w-full"
          enter="transition transition-[max-height] duration-500 ease-in"
          enterFrom="transform max-h-0"
          enterTo="transform max-h-screen"
          leave="transition transition-[max-height] duration-500 ease-out"
          leaveFrom="transform max-h-screen"
          leaveTo="transform max-h-0"
        >
          <div className="flex flex-col gap-y-2 w-full bg-white rounded-b-2xl p-8 pt-0">
            {showChart && medicalNecessityRow}

            {showChart && (
              <ShortTermGoalProgressGraph
                reportGoal={goal}
                selectedChart={selectItemForGoalChartType(
                  (goal.chartType as ApiReportGoalChartTypeChoices) ?? ApiReportGoalChartTypeChoices.Progress,
                )}
                setSelectedChart={() => {}}
                signedChartImageUrl={goal.signedChartImageUrl || ""}
                forReviewPage
              />
            )}

            {/* Goal Fields */}
            <TreatmentPlanForm
              templateFields={goalFields}
              nativeFieldValues={goalToFieldValues(goal, "-")}
              onNativeFieldSave={saveGoalFields}
              onCustomFieldSave={saveGoalCustomField}
              onImageUploaded={onGoalMutationSuccess}
              customFields={unwrapConnection(goal.customFields) ?? []}
              domain={domain}
              itemId={goal.id}
              getSuggestionCount={getSuggestionCount}
              fieldOverrides={{
                gas_level_1: null,
                gas_level_3: null,
                gas_level_4: null,
                ...(isGasEnabled
                  ? {
                      baseline_data: (
                        <GasPanel
                          GASData={{
                            baselineData: goal.baselineData || "",
                            masteryCriteria: goal.masteryCriteria || "",
                            gasLevel1: goal.gasLevel1 || "",
                            gasLevel3: goal.gasLevel3 || "",
                            gasLevel4: goal.gasLevel4 || "",
                          }}
                          goalId={goal.id}
                          reportId={report.id}
                          domainId={domain?.id ?? null}
                          learnerId={learnerId}
                          onSubmitCallback={onGoalMutationSuccess}
                          goalName={goal.goalName}
                          readOnly={reportIsCompleted}
                        />
                      ),
                      mastery_criteria: null,
                    }
                  : {}),
              }}
              isReportCompleted={reportIsCompleted}
            />

            {!showChart && medicalNecessityRow}

            {/* Targets */}
            {visibleTargets.length > 0 && (
              <div className="space-y-2 mt-2">
                {visibleTargets.map((target, targetIndex) => (
                  <TemplateTargetReviewCard
                    key={target.id}
                    target={target}
                    goal={goal}
                    report={report}
                    domain={domain}
                    targetFields={targetFields}
                    targetIndex={targetIndex}
                    goalIndex={goalIndex}
                    onSuccess={onGoalMutationSuccess}
                  />
                ))}
              </div>
            )}
          </div>
        </Transition>
      </div>
    </div>
  );
};
